import { useState } from 'react';
import { format, parse } from 'date-fns';
import { Check, Download } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { GUEST_STATUS_COLORS } from '../constants/bookingConst';
import { getCourtRate, parseTimeSlotStartHour, formatTimeSlotRange, getCourtLabel, describeEquipmentLine } from '../utils/bookingUtils';
import { isPackageBooking } from '../utils/packageUtils';
import PackageBookingBox from './PackageBookingBox';
import ConfirmationPaymentSummary from './ConfirmationPaymentSummary';
import '../css/ConfirmationScreen.css';
// Reuses UserInfoForm/CheckoutSummary's shared court/time/subtotal box
// layout (uif-* classes, active whenever an ancestor has the
// "user-info-form" class — see UserInfoForm.css) so the breakdown here
// looks identical to what the guest already saw at checkout.
import '../css/UserInfoForm.css';
import { formatMoney } from '../utils/currencyUtil';
import { downloadBookingReferencePdf } from '../utils/pdfReceipt';

// Shared jassen light palette (constants/bookingConst.js)
const COLORS = GUEST_STATUS_COLORS;

// 5. Confirmation Screen Component — a simple recap now that the payment
// receipt is uploaded earlier, on the Checkout step (see CheckoutSummary.jsx).
function ConfirmationScreen({ bookingData, onNewBooking }) {
  const { config } = useConfig();
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);

  /**
   * Saves the booking reference as a PDF. Moved here from the checkout
   * screen: there is nothing to keep until the booking is actually
   * submitted, and this is the screen the guest is told to hold on to.
   * The button is disabled while the logo is prepared so a double tap
   * doesn't download two copies.
   * @returns {Promise<void>}
   */
  const handleDownloadPdf = async () => {
    setIsPreparingPdf(true);
    try {
      await downloadBookingReferencePdf(bookingData, config, getCourtRate);
    } finally {
      setIsPreparingPdf(false);
    }
  };
  const courtName = config?.courtName || 'Your Court Name';
  // Club logo uploaded in Admin Config (config.logoUrl — see ConfigContext.jsx).
  const logoUrl = config?.logoUrl;

  // Multi-date bookings aren't supported yet, but this breakdown already
  // renders one card per date (see the .map below) — a future multi-date
  // booking would just need to populate bookingData.dates as an array of
  // { date, timeSlots } for it to show up correctly here with no further
  // UI changes. Until then this always resolves to a single-entry array
  // built from the existing single-date fields.
  const dateGroups = Array.isArray(bookingData.dates) && bookingData.dates.length > 0
    ? bookingData.dates
    : [{ date: bookingData.date, timeSlots: bookingData.timeSlots }];

  return (
    <div className="user-info-form confirmation-screen">
      <button type="button" className="uif-close conf-close" onClick={onNewBooking} aria-label="Close">&times;</button>

      {/* The admin-uploaded logo brands the confirmation instead of a generic
          check mark. The check stays as the fallback so clubs that haven't
          uploaded a logo yet still get a success badge rather than a gap. */}
      <div className={`conf-success-icon${logoUrl ? ' conf-success-icon--logo' : ''}`}>
        {logoUrl
          ? <img src={logoUrl} alt={courtName} className="conf-success-logo" />
          : <Check size={26} strokeWidth={3} />}
      </div>
      <h2 className="conf-success-title">Your reservation request has been received</h2>
      <p className="success-message">
        We're so glad you chose <strong>{courtName}</strong>. Your reservation is in, and our admins are verifying your payment— we'll confirm it shortly.
      </p>
      <p className="confirmation-note">
        Please make sure to take a screenshot of your confirmation id for your reference.
      </p>

      {/* The reference and its PDF, moved off the checkout screen. It reads
          as the thing to keep here, and it replaces the old plain
          "Confirmation ID:" line in the list below — the same number twice on
          one screen only makes the guest wonder which one to quote. */}
      <div className="conf-ref-block">
        <div className="conf-ref-label">Booking reference</div>
        <div className="conf-ref-value">{bookingData.confirmationId}</div>
      </div>

      <button type="button" className="conf-download-btn" onClick={handleDownloadPdf} disabled={isPreparingPdf}>
        <Download size={16} /> {isPreparingPdf ? 'Preparing PDF...' : 'Download booking reference (PDF)'}
      </button>

      <div className="conf-meta">
        <p><strong>Name:</strong> {bookingData.userName}</p>
        {bookingData.email && <p><strong>Email:</strong> {bookingData.email}</p>}
        <p><strong>Phone:</strong> {bookingData.phone}</p>
      </div>

      {/* An event package is one block at one price. The per-hour breakdown
          below would quote hourly court rates the guest was never charged,
          over hours that include the club's cleanup slot — see
          PackageBookingBox. */}
      {isPackageBooking(bookingData) ? (
        <PackageBookingBox bookingData={bookingData} />
      ) : dateGroups.map((group, idx) => {
        const courtGroups = {};
        Object.entries(group.timeSlots || {}).forEach(([timeSlot, courts]) => {
          (courts || []).forEach(court => {
            if (!courtGroups[court]) courtGroups[court] = [];
            // group.date scopes the lookup to the right weekday — rate bands
            // can be set per day (see getCourtRate in bookingUtils.js).
            courtGroups[court].push({ timeSlot, price: getCourtRate(config, court, timeSlot, null, group.date) });
          });
        });
        const sortedCourts = Object.keys(courtGroups).map(Number).sort((a, b) => a - b);
        sortedCourts.forEach(court => {
          courtGroups[court].sort((a, b) => parseTimeSlotStartHour(a.timeSlot) - parseTimeSlotStartHour(b.timeSlot));
        });
        const groupDateLabel = group.date
          ? format(parse(group.date, 'yyyy-MM-dd', new Date()), 'EEEE, MMMM d, yyyy')
          : '';

        return (
          <div className="uif-booking-box" key={group.date || idx}>
            <h3>{groupDateLabel}</h3>
            {sortedCourts.map(court => (
              <div key={court}>
                <div className="uif-court-lbl">{getCourtLabel(config, court)}</div>
                {courtGroups[court].map(row => (
                  <div className="uif-row" key={row.timeSlot}>
                    <span className="uif-time">{formatTimeSlotRange(row.timeSlot)}</span>
                    <span className="uif-fee">{formatMoney(row.price, config)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        );
      })}

      {bookingData.equipmentRental?.items?.length > 0 && (
        <div className="uif-booking-box">
          <h3>Equipment rental</h3>
          {bookingData.equipmentRental.items.map(item => (
            <div className="uif-row" key={item.id}>
              <span className="uif-time">{describeEquipmentLine(item)}</span>
              <span className="uif-fee">{formatMoney(Number(item.subtotal || 0), config)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Itemised so Total Paid adds up on screen — court time plus equipment
          alone falls short of it by the fee. Read off the record as ACTUALLY
          CHARGED rather than recomputed from config, so it can't disagree
          with the total if an admin changes the fee later. Package bookings
          store 0 (they're all-in), so this hides itself for them. */}
      {Number(bookingData.bookingFee) > 0 && (
        <div className="uif-fee-row">
          <span className="uif-time">Processing fee</span>
          <span className="uif-fee">{formatMoney(Number(bookingData.bookingFee), config)}</span>
        </div>
      )}

      <ConfirmationPaymentSummary bookingData={bookingData} config={config} />

      <p className="conf-status-row">
        <strong>Status:</strong> <span className="pending" style={{ color: COLORS.pending }}>Pending Approval</span>
      </p>

      <div className="tracking-url-instruction" style={{ margin: '20px 0', textAlign: 'center' }}>
        <p style={{ color: COLORS.textSecondary, fontSize: '0.95em' }}>
          To track your booking status anytime, go to:{' '}
          <a
            href={bookingData.trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: COLORS.neonLime, fontWeight: 'bold', wordBreak: 'break-all' }}
          >
            {bookingData.trackingUrl}
          </a>
        </p>
      </div>

      <button onClick={onNewBooking} className="btn-primary">
        Book Another Court
      </button>
    </div>
  );
}

export default ConfirmationScreen;
